import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ChevronLeft,
    Images,
    UserRound,
    Film,
    Search,
    Loader2,
    Camera,
    Shirt,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { entryThumbSrc, entryImageSrc } from "../lib/media";
import { Input } from "../components/ui/input";
import EpisodeFilter from "../components/EpisodeFilter";
import LookFilter from "../components/LookFilter";
import GalleryViewer from "../components/GalleryViewer";
import ProjectHeader from "../components/ProjectHeader";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";

/**
 * Iter-154 / Iter-154c — Gallery mode. Visual continuity browsing surface.
 *
 *   /projects/:projectId/gallery
 *
 * Two browsing modes (segmented control): "By Character" / "By Scene".
 * Iter-154c adds a filter bar (Episode + Look + Continuity State —
 * On Set / Fittings) that scopes BOTH the picker view AND the
 * drilldown views. Tapping an image opens a fullscreen viewer overlay
 * — never navigates away (the previous /images/{id} navigation was
 * unrouted and was dumping users to the homepage).
 *
 * Calm-UI: same palette as the rest of OnSetFlow, no aesthetic gloss.
 * Mobile-first; 3-column thumb grid at iPhone width.
 */
export default function GalleryPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    // Iter-154 — deep-link support: `?character={id}` opens the
    // character view directly (used by the inline Gallery shortcut on
    // CharacterDetail). `?scene={id}` would do the same for scenes.
    // Iter-154e — Filters now live in the URL (`?ep=` / `?look=` /
    // `?state=`) so a returnTo round-trip (Gallery → Scene Detail →
    // back to Gallery) preserves the operator's filter state without
    // any session-storage gymnastics.
    const deepLinkCharId = searchParams.get("character");
    const deepLinkSceneId = searchParams.get("scene");
    const [mode, setMode] = useState(
        deepLinkSceneId ? "scene" : "character",
    ); // 'character' | 'scene'

    // Iter-154c/e — Filters. Each scopes the picker query AND is passed
    // through to the drilldown endpoints. Initial value reads from URL.
    const episodeFilter = searchParams.get("ep") || null;
    const lookFilter = searchParams.get("look") || "";
    // Iter-158.13.2.20f.2 #9(b) — mode filter default from the canonical
    // `onsetflow:scenes-lens` localStorage bucket. When the URL does not
    // carry a `state` param (fresh entry into Gallery from OnSet or
    // Fittings surfaces), fall back to the last-active lens so OnSet
    // sessions land showing OnSet captures, and Fittings sessions land
    // showing Fittings captures — no inferred scene-ID semantics, no
    // change to Fittings core. An explicit `?state=` param (including
    // the empty "All" selection) always wins. `state` in
    // `{"onset","prep"}` is honoured; any other value falls back to
    // "All".
    //
    // Iter-158.13.2.20f.27c — Real-iPhone UAT surfaced that tapping
    // the "All" pill appeared dead. Root cause: `setStateFilter("")`
    // routed through `patchSearchParams({state: ""})` which DELETED
    // the `state` key entirely, so on next render `searchParams.has(
    // "state")` was false and the reader immediately re-fell-back to
    // the last-used lens from localStorage — snapping the filter back
    // to On Set / Fittings and making "All" un-selectable.
    // Repair: use `state=all` as an explicit URL sentinel meaning
    // "user chose All". `stateFilter` decodes it back to `""` so
    // the picker rows and gallery/pickers query behave identically
    // to the pre-repair "no state param" case, but the explicit
    // selection now sticks because the URL retains a truthy marker.
    const readLensDefault = () => {
        try {
            const v = window.localStorage.getItem("onsetflow:scenes-lens");
            return v === "onset" || v === "prep" ? v : "";
        } catch { return ""; }
    };
    const stateFilter = (() => {
        if (searchParams.has("state")) {
            const raw = searchParams.get("state") || "";
            if (raw === "all") return "";                    // explicit All
            if (raw === "onset" || raw === "prep") return raw;
            return "";                                       // unknown → treat as All
        }
        return readLensDefault();                            // no param → last-used lens
    })();

    const patchSearchParams = (patch) => {
        const next = new URLSearchParams(searchParams);
        for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === undefined || v === "") next.delete(k);
            else next.set(k, String(v));
        }
        setSearchParams(next, { replace: true });
    };
    const setEpisodeFilter = (v) => patchSearchParams({ ep: v });
    const setLookFilter = (v) => patchSearchParams({ look: v });
    // Iter-158.13.2.20f.27c — `setStateFilter` treats the empty
    // string ("All") specially: writes the `all` sentinel to the
    // URL rather than deleting the key. Keeps the .27b diagnosed
    // localStorage fallback from silently overriding the user's
    // explicit selection. "onset" / "prep" continue to write
    // through patchSearchParams as before.
    const setStateFilter = (v) => {
        if (v === "") {
            const next = new URLSearchParams(searchParams);
            next.set("state", "all");
            setSearchParams(next, { replace: true });
        } else {
            patchSearchParams({ state: v });
        }
    };

    const [episodes, setEpisodes] = useState([]);
    const [projectLooks, setProjectLooks] = useState([]);
    const [project, setProject] = useState(null);
    const [pickers, setPickers] = useState({ characters: [], scenes: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCharId, setSelectedCharId] = useState(deepLinkCharId || null);
    const [selectedSceneId, setSelectedSceneId] = useState(deepLinkSceneId || null);
    const [query, setQuery] = useState("");

    // Viewer state — held in the page so back-button / nav state never
    // leaves Gallery. The viewer reads a flat image list + index.
    const [viewer, setViewer] = useState({ open: false, images: [], index: 0 });

    // Iter-154e — `returnTo` path used when the user taps a character /
    // scene name and is routed into a detail page. Encodes the current
    // gallery URL (drilldown + filters) so the operator lands back
    // exactly where they were. Computed lazily inside handlers so it
    // always reflects the latest searchParams.
    const buildGalleryReturnTo = () => {
        const path = `${routeBase}/${projectId}/gallery${
            searchParams.toString() ? `?${searchParams.toString()}` : ""
        }`;
        return { path, label: "Back to Gallery" };
    };

    // Clearing the deep-link selection on back tap also clears the URL
    // query string so refresh / back-button behave naturally.
    const clearDeepLink = (key) => {
        if (!searchParams.get(key)) return;
        const next = new URLSearchParams(searchParams);
        next.delete(key);
        setSearchParams(next, { replace: true });
    };

    // Load episodes + project looks once. These shape the filter rail
    // and don't change frequently enough to refetch on filter change.
    // Iter-154g — also load project meta so we can render the shared
    // ProjectHeader (FlagBell, CaptureFab, project switcher, etc.)
    // and keep Gallery inside the standard app shell instead of a
    // bespoke cul-de-sac.
    useEffect(() => {
        let active = true;
        api.get(`${apiBase}/${projectId}`)
            .then(({ data }) => {
                if (active) setProject(data);
            })
            .catch(() => {
                /* non-fatal — header just shows "Project" placeholder */
            });
        api.get(`${apiBase}/${projectId}/episodes`)
            .then(({ data }) => {
                if (active) setEpisodes(data?.episodes || []);
            })
            .catch(() => {
                /* non-fatal — filter rail just hides */
            });
        api.get(`${apiBase}/${projectId}/looks`)
            .then(({ data }) => {
                if (active) setProjectLooks(data?.looks || []);
            })
            .catch(() => {
                /* non-fatal */
            });
        return () => {
            active = false;
        };
    }, [projectId, apiBase]);

    // Pickers — refetch on filter change.
    // Iter-154f — `include_images=12` turns picker rows into mini
    // contact sheets with up to 12 inline thumbnails each. One round
    // trip; rows enriched with scene+character context so the viewer
    // can render meta + nav links.
    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        const params = { include_images: 60 };
        if (episodeFilter) params.episode = episodeFilter;
        if (lookFilter) params.look = lookFilter;
        if (stateFilter) params.mode = stateFilter;
        api.get(`${apiBase}/${projectId}/gallery/pickers`, { params })
            .then(({ data }) => {
                if (!active) return;
                setPickers(data || { characters: [], scenes: [] });
            })
            .catch((err) => {
                if (!active) return;
                setError(formatApiError(err));
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [projectId, episodeFilter, lookFilter, stateFilter]);

    const filteredChars = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (pickers.characters || []).filter((c) =>
            !q || (c.name || "").toLowerCase().includes(q),
        );
    }, [pickers.characters, query]);

    const filteredScenes = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (pickers.scenes || []).filter(
            (s) =>
                !q ||
                (s.heading || "").toLowerCase().includes(q) ||
                String(s.scene_number || "").includes(q) ||
                String(s.episode || "").includes(q),
        );
    }, [pickers.scenes, query]);

    const showFilters = !selectedCharId && !selectedSceneId;

    // Iter-154g — Subtitle reflects the current drilldown context so
    // the shared header always communicates where the operator is.
    const headerSubtitle = selectedCharId
        ? "Gallery — Character"
        : selectedSceneId
            ? "Gallery — Scene"
            : "Gallery";

    return (
        <div className="min-h-screen flex flex-col bg-background text-neutral-100">
            {/* Iter-154g — Use the shared ProjectHeader so Gallery
                lives inside the normal app shell (FlagBell, CaptureFab,
                project switcher, global search, logout, theme toggle).
                Gallery is a section of the project, not a separate
                cul-de-sac. The in-page "back" affordance inside
                drilldown views is preserved beneath the header. */}
            <ProjectHeader projectName={project?.name} subtitle={headerSubtitle} />

            <main className="max-w-3xl mx-auto px-4 py-5 space-y-4 w-full">
                {/* Iter-154g — Identity block mirrors ScenesHub /
                    CastHub so Gallery reads as a normal project
                    section, not a bespoke screen. Only shown on the
                    landing view; inside drilldowns the section
                    heading carries the context instead. */}
                {showFilters ? (
                    <div className="mb-1 flex items-start gap-3">
                        <div
                            data-testid="gallery-identity-icon"
                            className="h-10 w-10 rounded-lg bg-secondary border border-white/10 text-neutral-200 flex items-center justify-center shrink-0"
                        >
                            <Images size={18} />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                                {project?.name || "Project"}
                            </div>
                            <h1
                                data-testid="gallery-hub-title"
                                className="font-display text-4xl font-black text-white mt-1"
                            >
                                Gallery
                            </h1>
                        </div>
                    </div>
                ) : null}
                {selectedCharId ? (
                    <CharacterGalleryView
                        projectId={projectId}
                        characterId={selectedCharId}
                        filters={{ episode: episodeFilter, look: lookFilter, mode: stateFilter }}
                        onBack={() => {
                            setSelectedCharId(null);
                            clearDeepLink("character");
                        }}
                        openViewer={(images, index) =>
                            setViewer({ open: true, images, index })
                        }
                        galleryReturnTo={buildGalleryReturnTo}
                    />
                ) : selectedSceneId ? (
                    <SceneGalleryView
                        projectId={projectId}
                        sceneId={selectedSceneId}
                        filters={{ look: lookFilter, mode: stateFilter }}
                        onBack={() => {
                            setSelectedSceneId(null);
                            clearDeepLink("scene");
                        }}
                        openViewer={(images, index) =>
                            setViewer({ open: true, images, index })
                        }
                        galleryReturnTo={buildGalleryReturnTo}
                    />
                ) : (
                    <>
                        <p className="text-[12px] text-neutral-400 leading-relaxed">
                            Visual continuity memory. Browse images grouped by
                            character or by scene. Scenes and characters with
                            no images are hidden.
                        </p>

                        <div
                            data-testid="gallery-mode-tabs"
                            className="grid grid-cols-2 gap-2"
                        >
                            <button
                                type="button"
                                onClick={() => setMode("character")}
                                data-testid="gallery-mode-character"
                                aria-pressed={mode === "character"}
                                className={`tap h-10 rounded-lg border text-[13px] font-medium inline-flex items-center justify-center gap-2 ${
                                    mode === "character"
                                        ? "bg-card border-white/15 text-neutral-100"
                                        : "bg-card/40 border-white/5 text-neutral-400"
                                }`}
                            >
                                <UserRound size={14} /> By Character
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("scene")}
                                data-testid="gallery-mode-scene"
                                aria-pressed={mode === "scene"}
                                className={`tap h-10 rounded-lg border text-[13px] font-medium inline-flex items-center justify-center gap-2 ${
                                    mode === "scene"
                                        ? "bg-card border-white/15 text-neutral-100"
                                        : "bg-card/40 border-white/5 text-neutral-400"
                                }`}
                            >
                                <Film size={14} /> By Scene
                            </button>
                        </div>
                    </>
                )}

                {/* Iter-154c — Filter rail. Hidden inside drilldown
                    views so the drilldown context isn't visually
                    busy; filters still apply because they're held in
                    page state and passed via props. */}
                {showFilters ? (
                    <div className="space-y-2.5">
                        <EpisodeFilter
                            episodes={episodes}
                            value={episodeFilter}
                            onChange={setEpisodeFilter}
                            testid="gallery-episode-filter"
                            className="mb-0"
                        />
                        {projectLooks.length > 0 ? (
                            <LookFilter
                                looks={projectLooks}
                                value={lookFilter}
                                onChange={setLookFilter}
                                testidPrefix="gallery-look-filter"
                            />
                        ) : null}
                        <StateFilter value={stateFilter} onChange={setStateFilter} />
                    </div>
                ) : null}

                {showFilters ? (
                    <div className="relative">
                        <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
                        />
                        <Input
                            placeholder={
                                mode === "character"
                                    ? "Search characters"
                                    : "Search scenes"
                            }
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="pl-9 h-10"
                            data-testid="gallery-search"
                        />
                    </div>
                ) : null}

                {showFilters ? (
                    loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 size={20} className="animate-spin text-neutral-500" />
                        </div>
                    ) : error ? (
                        <div className="text-[12px] text-rose-300 px-2">
                            {error}
                        </div>
                    ) : mode === "character" ? (
                        <PickerList
                            items={filteredChars}
                            emptyLabel={
                                episodeFilter || lookFilter || stateFilter
                                    ? "No characters match the current filters."
                                    : "No characters have continuity images yet."
                            }
                            testIdPrefix="gallery-char"
                            renderRow={(c) => (
                                <ContactSheetRow
                                    key={c.id}
                                    testId={`gallery-char-${c.id}`}
                                    heading={c.name}
                                    sublabel={c.team === "crowd" ? "Crowd" : "Main"}
                                    imageCount={c.image_count}
                                    groups={c.groups || []}
                                    groupKind="scene"
                                    projectId={projectId}
                                    onHeadingClick={() => setSelectedCharId(c.id)}
                                    onSeeAllClick={() => setSelectedCharId(c.id)}
                                    onThumbClick={(imgs, idx) =>
                                        setViewer({ open: true, images: imgs, index: idx })
                                    }
                                    onGroupHeadingClick={(group) =>
                                        group.scene_id &&
                                        navigate(
                                            `${routeBase}/${projectId}/scenes/${group.scene_id}`,
                                            { state: { returnTo: buildGalleryReturnTo() } },
                                        )
                                    }
                                />
                            )}
                        />
                    ) : (
                        <PickerList
                            items={filteredScenes}
                            emptyLabel={
                                episodeFilter || lookFilter || stateFilter
                                    ? "No scenes match the current filters."
                                    : "No scenes have continuity images yet."
                            }
                            testIdPrefix="gallery-scene"
                            renderRow={(s) => (
                                <ContactSheetRow
                                    key={s.id}
                                    testId={`gallery-scene-${s.id}`}
                                    heading={s.heading || `Scene ${s.scene_number}`}
                                    sublabel={`${s.episode ? `EP ${s.episode} · ` : ""}Scene ${s.scene_number}`}
                                    imageCount={s.image_count}
                                    groups={s.groups || []}
                                    groupKind="character"
                                    projectId={projectId}
                                    onHeadingClick={() => setSelectedSceneId(s.id)}
                                    onSeeAllClick={() => setSelectedSceneId(s.id)}
                                    onThumbClick={(imgs, idx) =>
                                        setViewer({ open: true, images: imgs, index: idx })
                                    }
                                    onGroupHeadingClick={(group) =>
                                        group.character_id &&
                                        navigate(
                                            `${routeBase}/${projectId}/characters/${group.character_id}`,
                                            { state: { returnTo: buildGalleryReturnTo() } },
                                        )
                                    }
                                />
                            )}
                        />
                    )
                ) : null}
            </main>

            <GalleryViewer
                open={viewer.open}
                projectId={projectId}
                images={viewer.images}
                index={viewer.index}
                onClose={() => setViewer((v) => ({ ...v, open: false }))}
                onIndexChange={(i) => setViewer((v) => ({ ...v, index: i }))}
                galleryReturnTo={buildGalleryReturnTo}
            />
        </div>
    );
}

function PickerList({ items, emptyLabel, renderRow, testIdPrefix }) {
    if (!items.length) {
        return (
            <div
                data-testid={`${testIdPrefix}-empty`}
                className="text-[12px] text-neutral-500 px-2 py-6 text-center"
            >
                {emptyLabel}
            </div>
        );
    }
    // Iter-154f — contact-sheet layout has more visual mass per row,
    // so a slightly larger vertical gap reads better than the dense
    // 2px-stack used by the old picker.
    return <div className="space-y-5">{items.map(renderRow)}</div>;
}

/**
 * Iter-154f/h — Contact-sheet row with scene/character grouping.
 *
 * Per the iter-154h spec: continuity drilldown must never show a flat
 * collage that mixes scenes / characters. Each row carries a `groups`
 * array (built server-side, scene-ordered or character-ordered) so the
 * images are visually segmented under their proper headings.
 *
 *   By Character mode → groups by scene (with EP banner above each
 *                       new episode); each group heading routes to
 *                       Scene Detail via the iter-154e returnTo.
 *   By Scene mode     → groups by character; each group heading
 *                       routes to Character Detail.
 *
 * Tap behaviour:
 *   - heading (Felix / Scene 2 · INT...) → drilldown view (full deep
 *     inspection, e.g. for characters with > 60 images).
 *   - group heading (Scene 2 · INT. / Felix · main) → corresponding
 *     detail page (Scene Detail or Character Detail).
 *   - thumbnail → fullscreen viewer.
 *
 * The viewer receives the FULL flat list of images for the row (across
 * all groups) so swipe still browses end-to-end continuity per
 * character / per scene without breaking on group boundaries.
 */
function ContactSheetRow({
    testId,
    heading,
    sublabel,
    imageCount,
    groups,
    groupKind, // 'scene' | 'character'
    projectId,
    onHeadingClick,
    onSeeAllClick,
    onThumbClick,
    onGroupHeadingClick,
}) {
    const groupList = groups || [];
    // Flat image list keeps viewer-swipe ordering = same display
    // order the user sees (groups already scene/character-sorted
    // server-side).
    const flat = [];
    for (const g of groupList) {
        for (const img of g.images || []) {
            flat.push(img);
        }
    }
    const totalShown = flat.length;
    const hiddenCount = Math.max(0, (imageCount || 0) - totalShown);

    // Episode banners (only meaningful for By Character mode). We
    // group scene-groups under their episode for visual hierarchy.
    let renderedEpisode = null;
    return (
        <section data-testid={testId} className="space-y-3">
            <header className="flex items-end justify-between gap-3">
                <button
                    type="button"
                    onClick={onHeadingClick}
                    data-testid={`${testId}-heading`}
                    className="tap text-left min-w-0 group"
                >
                    <div className="text-[14px] text-neutral-100 truncate font-semibold group-hover:underline underline-offset-2">
                        {heading}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mt-0.5">
                        {sublabel}
                    </div>
                </button>
                <div className="text-[10px] tabular-nums text-neutral-500 shrink-0">
                    {imageCount} image{imageCount === 1 ? "" : "s"} · across{" "}
                    {groupList.length} {groupKind}
                    {groupList.length === 1 ? "" : "s"}
                </div>
            </header>
            {groupList.length === 0 ? (
                <div
                    data-testid={`${testId}-thumbs-empty`}
                    className="text-[11px] text-neutral-600 italic px-1"
                >
                    No images under current filters.
                </div>
            ) : (
                <div className="space-y-3 pl-2 border-l border-white/5">
                    {groupList.map((g, idx) => {
                        const isScene = groupKind === "scene";
                        const groupLabel = isScene
                            ? `Scene ${g.scene_number || "?"} · ${g.scene_heading || ""}`
                            : `${g.character_name || "—"}`;
                        const groupSublabel = isScene
                            ? null
                            : g.team === "crowd"
                                ? "Crowd"
                                : "Main";
                        const groupKey = (isScene ? g.scene_id : g.character_id) || `idx-${idx}`;
                        const epKey = isScene ? g.scene_episode || null : null;
                        const showEpBanner =
                            isScene && epKey && epKey !== renderedEpisode;
                        if (showEpBanner) renderedEpisode = epKey;
                        return (
                            <div key={groupKey}>
                                {showEpBanner ? (
                                    <div
                                        data-testid={`${testId}-ep-${epKey}`}
                                        className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 font-semibold mb-1.5 mt-1"
                                    >
                                        EP {epKey}
                                    </div>
                                ) : null}
                                <button
                                    type="button"
                                    data-testid={`${testId}-group-${groupKey}`}
                                    onClick={() =>
                                        onGroupHeadingClick && onGroupHeadingClick(g)
                                    }
                                    className="tap block text-left text-[12px] text-neutral-300 mb-1.5 hover:text-white underline-offset-2 hover:underline transition-colors w-full"
                                >
                                    {isScene ? (
                                        <>
                                            <span className="font-medium">
                                                Scene {g.scene_number || "?"}
                                            </span>
                                            <span className="text-neutral-500">
                                                {" · "}
                                                {g.scene_heading || ""}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="font-medium">
                                                {groupLabel}
                                            </span>
                                            {groupSublabel ? (
                                                <span className="text-neutral-500 text-[10px] uppercase tracking-[0.15em] ml-1.5">
                                                    · {groupSublabel}
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </button>
                                <LookGroupedImages
                                    testId={`${testId}-group-${groupKey}`}
                                    images={g.images || []}
                                    flat={flat}
                                    projectId={projectId}
                                    onThumbClick={onThumbClick}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
            {hiddenCount > 0 ? (
                <button
                    type="button"
                    onClick={onSeeAllClick}
                    data-testid={`${testId}-see-all`}
                    className="tap text-[11px] uppercase tracking-[0.18em] text-neutral-400 hover:text-white underline-offset-2 hover:underline"
                >
                    See all {imageCount} →
                </button>
            ) : null}
        </section>
    );
}

/**
 * Iter-154g(look-labels) — Buckets a list of image entries by `look`
 * and renders one labelled sub-section per look. Calm visual: only
 * promote the look label to a sub-heading when there's more than one
 * distinct look — otherwise we render a single inline `Look: Hero`
 * line under the parent heading so the visual rhythm stays light.
 *
 * Missing look → "No look set" (matches the app's existing language
 * used by ProjectHome, ImageDetail, ContinuityBlock and StateStream).
 */
function LookGroupedImages({ testId, images, flat, projectId, onThumbClick }) {
    const buckets = new Map();
    // Iter-154j — Prefer `effective_look` (server-computed pair
    // inheritance) over the raw `look` field so the gallery's
    // grouping mirrors the Look line that ContinuityBlock surfaces
    // in Scene Detail. If a (character, scene) pair has exactly
    // one distinct non-null look anywhere in its history, all
    // null-look entries inherit it.
    for (const img of images || []) {
        const key = img.effective_look || img.look || "__none__";
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(img);
    }
    const lookKeys = Array.from(buckets.keys());
    // Single look → inline label + flat grid (calm — no need to split).
    if (lookKeys.length <= 1) {
        const only = lookKeys[0] || "__none__";
        const lookLabel = only === "__none__" ? "No look set" : only;
        return (
            <>
                <div
                    data-testid={`${testId}-look-${only === "__none__" ? "none" : only}`}
                    className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5"
                >
                    Look: <span className="text-neutral-300">{lookLabel}</span>
                </div>
                <ThumbGrid
                    images={images || []}
                    flat={flat}
                    projectId={projectId}
                    onThumbClick={onThumbClick}
                />
            </>
        );
    }
    // Multiple looks → sub-section per look. Alphabetised; "no look"
    // sinks to the bottom so it doesn't displace the named looks.
    lookKeys.sort((a, b) => {
        if (a === "__none__") return 1;
        if (b === "__none__") return -1;
        return a.localeCompare(b);
    });
    return (
        <div className="space-y-2.5">
            {lookKeys.map((k) => {
                const looksImages = buckets.get(k);
                const label = k === "__none__" ? "No look set" : k;
                const tidSuffix = k === "__none__" ? "none" : k;
                return (
                    <div key={k}>
                        <div
                            data-testid={`${testId}-look-${tidSuffix}`}
                            className="text-[11px] font-medium text-neutral-200 mb-1"
                        >
                            {label}
                            <span className="ml-1.5 text-neutral-500 text-[10px] tabular-nums">
                                · {looksImages.length}
                            </span>
                        </div>
                        <ThumbGrid
                            images={looksImages}
                            flat={flat}
                            projectId={projectId}
                            onThumbClick={onThumbClick}
                        />
                    </div>
                );
            })}
        </div>
    );
}

function ThumbGrid({ images, flat, projectId, onThumbClick }) {
    return (
        <div className="grid grid-cols-4 gap-1.5">
            {(images || []).map((e) => {
                const idxInFlat = (flat || []).findIndex((x) => x.id === e.id);
                return (
                    <ImageTile
                        key={e.id}
                        entry={e}
                        projectId={projectId}
                        onClick={() =>
                            onThumbClick(flat || images, idxInFlat >= 0 ? idxInFlat : 0)
                        }
                    />
                );
            })}
        </div>
    );
}

/**
 * Iter-154c — Continuity State filter. On Set vs Fittings. Future-
 * ready slots for Main / Crowd (rendered disabled so the affordance
 * exists but doesn't ship behaviour yet — keeps the layout stable
 * for the iter that wires team filtering).
 */
function StateFilter({ value, onChange }) {
    const options = [
        { id: "", label: "All", icon: null },
        { id: "onset", label: "On Set", icon: Camera },
        { id: "prep", label: "Fittings", icon: Shirt },
    ];
    return (
        <div
            data-testid="gallery-state-filter"
            className="flex items-center gap-2"
        >
            <span className="shrink-0 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                State
            </span>
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1 min-w-0 pb-0.5">
                {options.map((o) => {
                    const active = value === o.id;
                    const Icon = o.icon;
                    return (
                        <button
                            key={o.id || "all"}
                            type="button"
                            data-testid={`gallery-state-filter-${o.id || "all"}`}
                            data-active={active ? "true" : "false"}
                            onClick={() => onChange(o.id)}
                            className={`tap shrink-0 h-8 px-3 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em] border inline-flex items-center gap-1.5 ${
                                active
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-transparent text-neutral-300 border-white/10 hover:border-white/25 hover:text-white"
                            }`}
                        >
                            {Icon ? <Icon size={12} /> : null}
                            {o.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function ImageTile({ entry, projectId, onClick }) {
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    // Iter-154d — Use the same helper Recent Activity / Scene detail /
    // Character detail use to resolve thumbnails. The backend
    // `_gallery_entry_row` returns a RELATIVE `thumbnail_url` (no host,
    // no JWT) which can't be used as <img src> directly. `entryThumbSrc`
    // builds the fully-qualified `${REACT_APP_BACKEND_URL}/.../file?...`
    // URL with the user's token appended, AND falls back to legacy
    // inline base64 data when no media_id exists.
    const src = entryThumbSrc(projectId, entry, apiBase) || entryImageSrc(projectId, entry, "display", apiBase);
    const [errored, setErrored] = useState(false);
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`gallery-image-${entry.id}`}
            className="tap aspect-square overflow-hidden rounded-md border border-white/5 bg-secondary/30 hover:border-white/15 relative"
        >
            {src && !errored ? (
                <img
                    src={src}
                    alt={entry.state_label || ""}
                    loading="lazy"
                    onError={() => setErrored(true)}
                    className="w-full h-full object-cover"
                />
            ) : (
                <div
                    data-testid={`gallery-image-missing-${entry.id}`}
                    className="w-full h-full flex flex-col items-center justify-center gap-1 text-neutral-600"
                >
                    <Images size={16} />
                    <span className="text-[9px] uppercase tracking-[0.2em]">
                        unavailable
                    </span>
                </div>
            )}
        </button>
    );
}

function CharacterGalleryView({ projectId, characterId, filters, onBack, openViewer, galleryReturnTo }) {
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const reqRef = useRef(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        const myReq = ++reqRef.current;
        const params = {};
        if (filters.episode) params.episode = filters.episode;
        if (filters.look) params.look = filters.look;
        if (filters.mode) params.mode = filters.mode;
        api.get(`${apiBase}/${projectId}/gallery/by-character/${characterId}`, { params })
            .then(({ data: payload }) => {
                if (!active || reqRef.current !== myReq) return;
                setData(payload);
            })
            .catch((err) => {
                if (!active || reqRef.current !== myReq) return;
                toast.error(formatApiError(err));
            })
            .finally(() => {
                if (active && reqRef.current === myReq) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [projectId, characterId, filters.episode, filters.look, filters.mode, apiBase]);

    // Flatten image list across scenes — needed so the fullscreen
    // viewer can swipe across the entire character's continuity
    // memory in scene order.
    const flatImages = useMemo(() => {
        if (!data?.scenes) return [];
        const out = [];
        for (const sc of data.scenes) {
            for (const img of sc.images || []) {
                out.push({
                    ...img,
                    character_name: data.character?.name,
                    scene_number: sc.scene_number,
                    scene_episode: sc.episode,
                    scene_heading: sc.heading,
                });
            }
        }
        return out;
    }, [data]);

    // Iter-157J.4 — Character-level fittings (scene_id=None) come
    // back as a pseudo-scene group flagged `is_unassigned`. Extract
    // it so the FE renders it under a distinct "Fittings
    // (Unassigned)" banner ABOVE the episode/scene groupings —
    // mirroring the character drawer's mental model
    // (fittings happen first, scenes happen later).
    const unassignedGroup = useMemo(() => {
        for (const sc of data?.scenes || []) {
            if (sc.is_unassigned) return sc;
        }
        return null;
    }, [data]);

    // Group scenes by episode for nicer headings.
    const groups = useMemo(() => {
        const out = new Map();
        for (const sc of data?.scenes || []) {
            if (sc.is_unassigned) continue; // Rendered separately above.
            const key = sc.episode || "__none__";
            if (!out.has(key)) out.set(key, []);
            out.get(key).push(sc);
        }
        return Array.from(out.entries());
    }, [data]);

    const indexOfEntry = (entryId) =>
        flatImages.findIndex((e) => e.id === entryId);

    return (
        <div>
            <button
                type="button"
                onClick={onBack}
                data-testid="gallery-back-to-picker"
                className="tap inline-flex items-center gap-1 text-[12px] text-neutral-400 hover:text-neutral-200 mb-3"
            >
                <ChevronLeft size={14} /> back
            </button>
            {loading ? (
                <div className="flex justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-neutral-500" />
                </div>
            ) : data ? (
                <>
                    <h2
                        className="text-[15px] font-semibold mb-1"
                        data-testid="gallery-char-title"
                    >
                        {data.character.name}
                    </h2>
                    {/* Iter-157J.4 — Scene count must exclude the
                        Fittings (Unassigned) pseudo-group; it is
                        not a real scene. */}
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-4">
                        {data.total_images} image
                        {data.total_images === 1 ? "" : "s"}
                        {(() => {
                            const realScenes = (data.scenes || []).filter(
                                (s) => !s.is_unassigned,
                            ).length;
                            const fitCount = unassignedGroup
                                ? (unassignedGroup.images || []).length
                                : 0;
                            const bits = [];
                            if (realScenes > 0) {
                                bits.push(
                                    ` · across ${realScenes} scene${realScenes === 1 ? "" : "s"}`,
                                );
                            }
                            if (fitCount > 0) {
                                bits.push(
                                    ` · ${fitCount} fitting${fitCount === 1 ? "" : "s"}`,
                                );
                            }
                            return bits.join("");
                        })()}
                    </div>
                    {data.scenes.length === 0 ? (
                        <div
                            data-testid="gallery-char-drilldown-empty"
                            className="text-[12px] text-neutral-500 px-2 py-6 text-center"
                        >
                            No images match the current filters.
                        </div>
                    ) : null}
                    {/* Iter-157J.4 — Fittings (Unassigned) pseudo-scene
                        group rendered ABOVE the episode/scene tree.
                        These are character-level fitting captures
                        that have not yet been attached to any scene.
                        Calm amber banner mirrors the prep drawer
                        treatment so the visual language is
                        consistent across the app. */}
                    {unassignedGroup ? (
                        <section
                            data-testid="gallery-char-drilldown-unassigned"
                            className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-amber-200">
                                    Fittings (Unassigned)
                                </div>
                                <span className="text-[10px] text-neutral-400 tabular-nums">
                                    {(unassignedGroup.images || []).length}
                                </span>
                            </div>
                            <div className="text-[11px] text-neutral-400 mb-2 leading-relaxed">
                                Character-level fitting photos — not yet attached to a scene.
                            </div>
                            <LookGroupedImages
                                testId="gallery-char-drilldown-unassigned-group"
                                images={unassignedGroup.images || []}
                                flat={flatImages}
                                projectId={projectId}
                                onThumbClick={(imgs, idx) => openViewer(imgs, idx)}
                            />
                        </section>
                    ) : null}
                    {groups.map(([epKey, scenes]) => (
                        <section key={epKey} className="mb-6">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                                {epKey === "__none__" ? "Scenes" : `EP ${epKey}`}
                            </div>
                            {scenes.map((sc) => (
                                <div key={sc.scene_id} className="mb-4">
                                    {/* Iter-154e — scene heading is a
                                        tappable link into Scene Detail.
                                        `returnTo` round-trips the user
                                        back to this exact Gallery URL
                                        (filters + drilldown preserved). */}
                                    <button
                                        type="button"
                                        data-testid={`gallery-scene-link-${sc.scene_id}`}
                                        onClick={() =>
                                            navigate(
                                                `${routeBase}/${projectId}/scenes/${sc.scene_id}`,
                                                { state: { returnTo: galleryReturnTo?.() } },
                                            )
                                        }
                                        className="tap block text-left text-[12px] text-neutral-300 mb-2 hover:text-white underline-offset-2 hover:underline transition-colors"
                                    >
                                        Scene {sc.scene_number} ·{" "}
                                        <span className="text-neutral-500">
                                            {sc.heading}
                                        </span>
                                    </button>
                                    {/* Iter-154g(look-labels) — Sub-group
                                        the scene's images by look so the
                                        operator immediately sees which
                                        look frame belongs to. */}
                                    <LookGroupedImages
                                        testId={`gallery-char-drilldown-scene-${sc.scene_id}`}
                                        images={sc.images || []}
                                        flat={flatImages}
                                        projectId={projectId}
                                        onThumbClick={(imgs, idx) => openViewer(imgs, idx)}
                                    />
                                </div>
                            ))}
                        </section>
                    ))}
                </>
            ) : null}
        </div>
    );
}

function SceneGalleryView({ projectId, sceneId, filters, onBack, openViewer, galleryReturnTo }) {
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const reqRef = useRef(0);
    useEffect(() => {
        let active = true;
        setLoading(true);
        const myReq = ++reqRef.current;
        const params = {};
        if (filters.look) params.look = filters.look;
        if (filters.mode) params.mode = filters.mode;
        api.get(`${apiBase}/${projectId}/gallery/by-scene/${sceneId}`, { params })
            .then(({ data: payload }) => {
                if (active && reqRef.current === myReq) setData(payload);
            })
            .catch((err) => {
                if (active && reqRef.current === myReq) toast.error(formatApiError(err));
            })
            .finally(() => {
                if (active && reqRef.current === myReq) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [projectId, sceneId, filters.look, filters.mode, apiBase]);

    // Flatten + carry context — same idea as CharacterGalleryView.
    const flatImages = useMemo(() => {
        if (!data?.characters) return [];
        const out = [];
        for (const c of data.characters) {
            for (const img of c.images || []) {
                out.push({
                    ...img,
                    character_name: c.character_name,
                    scene_number: data.scene?.scene_number,
                    scene_episode: data.scene?.episode,
                    scene_heading: data.scene?.heading,
                });
            }
        }
        return out;
    }, [data]);

    const indexOfEntry = (entryId) =>
        flatImages.findIndex((e) => e.id === entryId);

    return (
        <div>
            <button
                type="button"
                onClick={onBack}
                data-testid="gallery-back-to-picker"
                className="tap inline-flex items-center gap-1 text-[12px] text-neutral-400 hover:text-neutral-200 mb-3"
            >
                <ChevronLeft size={14} /> back
            </button>
            {loading ? (
                <div className="flex justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-neutral-500" />
                </div>
            ) : data ? (
                <>
                    <h2
                        className="text-[15px] font-semibold mb-1"
                        data-testid="gallery-scene-title"
                    >
                        {data.scene.heading || `Scene ${data.scene.scene_number}`}
                    </h2>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-4">
                        {data.scene.episode ? `EP ${data.scene.episode} · ` : ""}
                        Scene {data.scene.scene_number} · {data.total_images} image
                        {data.total_images === 1 ? "" : "s"}
                    </div>
                    {data.characters.length === 0 ? (
                        <div
                            data-testid="gallery-scene-drilldown-empty"
                            className="text-[12px] text-neutral-500 px-2 py-6 text-center"
                        >
                            No images match the current filters.
                        </div>
                    ) : null}
                    {data.characters.map((c) => (
                        <div key={c.character_id} className="mb-5">
                            {/* Iter-154e — character name routes to
                                Character Detail; returnTo round-trips
                                back into this Gallery URL. */}
                            <button
                                type="button"
                                data-testid={`gallery-character-link-${c.character_id}`}
                                onClick={() =>
                                    navigate(
                                        `${routeBase}/${projectId}/characters/${c.character_id}`,
                                        { state: { returnTo: galleryReturnTo?.() } },
                                    )
                                }
                                className="tap block text-left text-[12px] text-neutral-300 mb-2 hover:text-white underline-offset-2 hover:underline transition-colors"
                            >
                                {c.character_name}{" "}
                                <span className="text-neutral-500 text-[10px] uppercase tracking-[0.15em] ml-1">
                                    · {c.team === "crowd" ? "crowd" : "main"}
                                </span>
                            </button>
                            {/* Iter-154g(look-labels) — Same look
                                sub-grouping applied to scene-drilldown
                                character blocks. */}
                            <LookGroupedImages
                                testId={`gallery-scene-drilldown-char-${c.character_id}`}
                                images={c.images || []}
                                flat={flatImages}
                                projectId={projectId}
                                onThumbClick={(imgs, idx) => openViewer(imgs, idx)}
                            />
                        </div>
                    ))}
                </>
            ) : null}
        </div>
    );
}
